<template>
  <div class="worker-dashboard">
    <!-- Formulaire Surplus -->
    <surplus-form v-if="showSurplusForm" @close="showSurplusForm = false" />

    <!-- Dashboard Principal -->
    <div v-else class="space-y-4">
      <dashboard-context-header title="SERVICE CANTINE" @context-change="onContextChange" />

      <!-- Menu du jour -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">📋 MENU DU JOUR</h3>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Entrée</span>
            <span class="font-semibold">Salade composée</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Plat</span>
            <span class="font-semibold">Poulet rôti + riz</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Accomp.</span>
            <span class="font-semibold">Haricots verts</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Dessert</span>
            <span class="font-semibold">Yaourt nature</span>
          </div>
        </div>
      </div>

      <!-- Effectifs -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-bold text-gray-800">👥 EFFECTIFS</h3>
          <button @click="editServed" class="text-blue-600 text-sm font-medium">✏️ Modifier</button>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-blue-50 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold text-blue-600">142</div>
            <div class="text-xs text-gray-600">Prévus</div>
          </div>
          <div class="bg-green-50 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold text-green-600">{{ served }}</div>
            <div class="text-xs text-gray-600">Servis</div>
          </div>
        </div>
      </div>

      <!-- Actions rapides -->
      <div class="space-y-3">
        <h3 class="font-bold text-gray-800">⚡ ACTIONS RAPIDES</h3>
        <button
          @click="showSurplusForm = true"
          class="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg p-4 font-semibold text-left shadow-md transition"
        >
          📦 Signaler un surplus
        </button>
        <button
          @click="signalProblem"
          class="w-full bg-red-500 hover:bg-red-600 text-white rounded-lg p-4 font-semibold text-left shadow-md transition"
        >
          ⚠️ Signaler un problème
        </button>
        <button
          @click="addRemarks"
          class="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-lg p-4 font-semibold text-left shadow-md transition"
        >
          💬 Remarques sur le service
        </button>
      </div>

      <!-- Ambiance salle -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">🌡️ AMBIANCE SALLE</h3>
        <div class="flex justify-around">
          <button
            @click="setAmbiance('calm')"
            :class="{ 'bg-green-50': ambiance === 'calm' }"
            class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-green-50 transition"
          >
            <span class="text-3xl">😊</span>
            <span class="text-xs text-gray-600">Calme</span>
          </button>
          <button
            @click="setAmbiance('normal')"
            :class="{ 'bg-yellow-50': ambiance === 'normal' }"
            class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-yellow-50 transition"
          >
            <span class="text-3xl">😐</span>
            <span class="text-xs text-gray-600">Normal</span>
          </button>
          <button
            @click="setAmbiance('agitated')"
            :class="{ 'bg-red-50': ambiance === 'agitated' }"
            class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-red-50 transition"
          >
            <span class="text-3xl">😟</span>
            <span class="text-xs text-gray-600">Agité</span>
          </button>
        </div>
      </div>
      <!-- NOUVEAU : Alertes communications (EPIC 2) -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3 flex items-center gap-2">
          📢 Messages
          <span v-if="unreadMessages > 0" class="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
            {{ unreadMessages }}
          </span>
        </h3>
        <button @click="showComms = true" class="w-full bg-blue-500 text-white rounded-lg p-2 text-sm font-semibold">
          Voir les communications
        </button>
      </div>

      <!-- NOUVEAU : Saisie gaspillage (EPIC 4) -->
      <div class="bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
        <h3 class="font-bold text-gray-800 mb-2 flex items-center gap-2">♻️ Fin de service</h3>
        <p class="text-sm text-gray-600 mb-3">Pesée du gaspillage</p>
        <button
          @click="showWasteInput = true"
          class="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg p-3 font-semibold"
        >
          🗑️ Mesurer le gaspillage
        </button>
      </div>

      <!-- Modals -->
      <communication-panel v-if="showComms" @close="showComms = false" />
      <div v-if="showWasteInput">
        <waste-input-panel :canteen-id="canteenId" variant="barquette" />
      </div>
    </div>
  </div>
</template>

<script>
import SurplusForm from "./SurplusForm.vue"
import CommunicationPanel from "@/components/CommunicationPanel.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"

export default {
  name: "WorkerDashboard",
  components: {
    SurplusForm,
    CommunicationPanel,
    WasteInputPanel,
    DashboardContextHeader,
  },
  data() {
    return {
      showSurplusForm: false,
      served: 138,
      ambiance: null,
      showComms: false,
      showWasteInput: false,
      unreadMessages: 2,
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
    }
  },
  methods: {
    onContextChange({ canteenId, selectedDate, selectedService }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
    },
    editServed() {
      const newValue = prompt("Nombre d'élèves servis:", this.served)
      if (newValue) {
        this.served = parseInt(newValue)
      }
    },
    signalProblem() {
      alert("Signalement de problème")
    },
    addRemarks() {
      alert("Ajout de remarques")
    },
    setAmbiance(type) {
      this.ambiance = type
      alert(`Ambiance enregistrée : ${type}`)
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.gap-4 {
  gap: 1rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.items-center {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.justify-around {
  justify-content: space-around;
}
.flex-col {
  flex-direction: column;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.from-purple-500 {
  --tw-gradient-from: #a855f7;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-purple-600 {
  --tw-gradient-to: #9333ea;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.bg-white {
  background-color: #ffffff;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-green-50 {
  background-color: #f0fdf4;
}
.bg-yellow-50 {
  background-color: #fefce8;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-orange-500 {
  background-color: #f97316;
}
.bg-orange-600 {
  background-color: #ea580c;
}
.hover\:bg-orange-600:hover {
  background-color: #ea580c;
}
.hover\:bg-green-50:hover {
  background-color: #f0fdf4;
}
.hover\:bg-yellow-50:hover {
  background-color: #fefce8;
}
.hover\:bg-red-50:hover {
  background-color: #fef2f2;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-red-600 {
  background-color: #dc2626;
}
.hover\:bg-red-600:hover {
  background-color: #dc2626;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.bg-blue-600 {
  background-color: #2563eb;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.text-white {
  color: #ffffff;
}
.text-blue-600 {
  color: #2563eb;
}
.text-green-600 {
  color: #16a34a;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.font-medium {
  font-weight: 500;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-left {
  text-align: left;
}
.text-center {
  text-align: center;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.w-full {
  width: 100%;
}
.opacity-90 {
  opacity: 0.9;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:hover {
  opacity: 0.9;
}
</style>
